import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import { AppLayout } from "./layouts/AppLayout";
import { DashboardLayout } from "./layouts/dashboard/DashboardLayout";
import { billingRoutes } from "./views/admin/billing/routes";
import { ConfigurationView } from "./views/admin/configuration/ConfigurationView";
import { CustomerCreateView } from "./views/admin/customers/CustomerCreateView";
import { CustomerEditView } from "./views/admin/customers/CustomerEditView";
import { CustomersView } from "./views/admin/customers/CustomersView";
import { CustomerView } from "./views/admin/customers/CustomerView";
import { DashboardUserCreateView } from "./views/admin/dashboard-users/DashboardUserCreateView";
import { DashboardUserEditView } from "./views/admin/dashboard-users/DashboardUserEditView";
import { DashboardUsersView } from "./views/admin/dashboard-users/DashboardUsersView";
import { DashboardUserView } from "./views/admin/dashboard-users/DashboardUserView";
import { OpenApiProductsView } from "./views/admin/open-api-products/OpenApiProductsView";
import { OpenApiProductView } from "./views/admin/open-api-products/OpenApiProductView";
import { ApiCredentialCreateView } from "./views/api-credentials/ApiCredentialCreateView";
import { ApiCredentialsView } from "./views/api-credentials/ApiCredentialsView";
import { ApiExamplesView } from "./views/ApiExamplesView";
import { CustomerProductsView } from "./views/customer-products/CustomerProductsView";
import { Error404View } from "./views/error/Error404View";
import { IndexView } from "./views/index/IndexView";
import { PrintAssetsView } from "./views/print-asset/PrintAssetsView";
import { PrintAssetView } from "./views/print-asset/PrintAssetView";
import { SettingsEditAccountPasswordView } from "./views/settings/SettingsEditAccountPasswordView";
import { SettingsEditAccountView } from "./views/settings/SettingsEditAccountView";
import { SettingsView } from "./views/settings/SettingsView";
import { DesignLayout } from "./views/temporary/DesignLayout";
import { DesignV2View } from "./views/temporary/DesignV2View";
import { DesignView } from "./views/temporary/DesignView";
import { GraphQL } from "@rpi/openapi-api";
import { UploadsView } from "./views/admin/uploads/UploadsView";
import { UploadFilesView } from "./views/admin/uploads/UploadFilesView";
import { UploadsCollectionView } from "./views/admin/uploads/UploadsCollectionView";
import { AuthContainer } from "./components/AuthContainer";
import { AuthenticatorView } from "./views/auth/AuthenticatorView";
import { MantineProvider } from "@mantine/core";
import { rpaTheme } from "./theme/rpaTheme";
import { SetupPayment } from "./views/onBoarding/SetupPayment";
import { OnBoarding } from "./views/onBoarding/OnBoarding";
import { CustomerEditTypeView } from "./views/admin/customers/CustomerEditTypeView";
import { withHandle } from "./utils/route.util";
import {
  productCreateRoute,
  productDetailsRoute,
  productEditRoute,
  productFiltersRoute,
} from "./views/customer-products/routes";
import { DashboardView } from "./views/dashboard/DashboardView";
import {
  OpenApiProductSkuEditor,
  OpenApiProductSkuNavigator,
} from "./views/admin/open-api-products/OpenApiProductSkuNavigator";
import { ordersRoutes } from "./views/orders/routes";

export const DASHBOARD_PATH = "/dashboard";
const ADMIN_PATH = "admin";
export const ADMIN_DASHBOARD_PATH = `${DASHBOARD_PATH}/${ADMIN_PATH}`;

export const router = createBrowserRouter([
  {
    path: "/",
    element: <AppLayout.Private />,
    errorElement: <Error404View />,
    children: [
      {
        path: "onboarding",
        element: (
          <MantineProvider theme={rpaTheme} withCSSVariables withGlobalStyles>
            <AuthContainer>
              <Outlet />
            </AuthContainer>
          </MantineProvider>
        ),
        children: [
          {
            index: true,
            element: <OnBoarding />,
            ...withHandle("onboarding"),
          },
          {
            path: "setup-payment",
            element: <SetupPayment />,
            ...withHandle("setup-payment"),
          },
        ],
      },
      {
        path: "dashboard",
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <DashboardView />,
            ...withHandle("dashboard"),
          },
          {
            path: "settings",
            element: <SettingsView />,
            ...withHandle("settings"),
            children: [
              {
                path: "edit-account",
                element: <SettingsEditAccountView />,
                ...withHandle("settings-edit-account"),
              },
              {
                path: "edit-password",
                element: <SettingsEditAccountPasswordView />,
                ...withHandle("settings-edit-account"),
              },
            ],
          },
          {
            path: "customer-products",
            element: <CustomerProductsView />,
            ...withHandle("customer-products"),
            children: [
              productCreateRoute,
              productDetailsRoute,
              ...(productFiltersRoute ? [productFiltersRoute] : []),
              ...(productEditRoute ? [productEditRoute] : []),
            ],
          },
          {
            path: "print-assets",
            children: [
              {
                index: true,
                element: <PrintAssetsView />,
                ...withHandle("print-assets"),
              },
              {
                path: ":id",
                element: <PrintAssetView />,
                ...withHandle("print-asset-detail"),
              },
            ],
          },
          ...ordersRoutes,
          {
            path: "api-credentials",
            element: <ApiCredentialsView />,
            ...withHandle("api-credentials"),
            children: [
              {
                path: "create",
                ...withHandle("api-credential-create", {
                  requiredRole: [GraphQL.DashboardUserRole.CustomerAdmin, GraphQL.DashboardUserRole.User],
                }),
                element: <ApiCredentialCreateView />,
              },
            ],
          },
          {
            path: "api-examples",
            element: <ApiExamplesView />,
            ...withHandle("api-examples"),
          },
          {
            path: ADMIN_PATH,
            ...withHandle(ADMIN_PATH, {
              requiredRole: [GraphQL.DashboardUserRole.SuperAdmin, GraphQL.DashboardUserRole.CustomerAdmin],
            }),
            children: [
              {
                path: "customers",
                element: <CustomersView />,
                ...withHandle("admin-customers", {
                  requiredRole: GraphQL.DashboardUserRole.SuperAdmin,
                }),
                children: [
                  {
                    path: "create",
                    element: <CustomerCreateView />,
                    ...withHandle("admin-customer-create"),
                  },
                ],
              },
              {
                path: "customers/:customerPublicId",
                element: <CustomerView />,
                ...withHandle("admin-customer-detail", {
                  requiredRole: GraphQL.DashboardUserRole.SuperAdmin,
                }),
                children: [
                  {
                    path: "edit",
                    element: <CustomerEditView />,
                    ...withHandle("admin-customer-edit"),
                  },
                  {
                    path: "edit-type",
                    element: <CustomerEditTypeView />,
                    ...withHandle("admin-customer-edit-type"),
                  },
                ],
              },
              {
                path: "open-api-products",
                ...withHandle("admin-open-api-products", {
                  requiredRole: GraphQL.DashboardUserRole.SuperAdmin,
                }),
                element: <OpenApiProductsView />,
                children: [
                  {
                    path: "create",
                    element: <OpenApiProductSkuNavigator />,
                    ...withHandle("admin-open-api-product-create"),
                  },
                  {
                    path: "edit/:id",
                    element: <OpenApiProductSkuEditor />,
                    ...withHandle("admin-open-api-product-edit"),
                  },
                  {
                    path: ":id",
                    element: <OpenApiProductView />,
                    ...withHandle("admin-open-api-product-detail"),
                  },
                ],
              },
              {
                path: "dashboard-users",
                element: <DashboardUsersView />,
                ...withHandle("admin-dashboard-users"),
                children: [
                  {
                    path: "create",
                    element: <DashboardUserCreateView />,
                    ...withHandle("admin-dashboard-user-create"),
                  },
                  {
                    path: ":id",
                    element: <DashboardUserView />,
                    ...withHandle("admin-dashboard-user-detail"),
                  },
                  {
                    path: ":id/edit",
                    element: <DashboardUserEditView />,
                    ...withHandle("admin-dashboard-user-edit"),
                  },
                ],
              },
              ...billingRoutes,
              {
                path: "uploads",
                element: <UploadsView />,
                ...withHandle("admin-uploads", {
                  requiredRole: GraphQL.DashboardUserRole.SuperAdmin,
                }),
                children: [
                  {
                    path: "upload",
                    element: <UploadFilesView />,
                    ...withHandle("admin-upload-files"),
                  },
                ],
              },
              {
                path: "uploads",
                ...withHandle("uploads", {
                  requiredRole: GraphQL.DashboardUserRole.SuperAdmin,
                }),
                children: [
                  {
                    path: "customers/:customerPublicId/collections/:collectionId",
                    element: <UploadsCollectionView />,
                    ...withHandle("admin-uploads-collection"),
                  },
                ],
              },
              {
                path: "configuration",
                ...withHandle("configuration", {
                  requiredRole: GraphQL.DashboardUserRole.SuperAdmin,
                }),
                children: [
                  {
                    index: true,
                    element: <ConfigurationView />,
                    ...withHandle("admin-configuration"),
                  },
                ],
              },
            ],
          },
          {
            path: "*",
            element: <Error404View root={false} hideButton />,
            ...withHandle("not-found"),
          },
        ],
      },
    ],
  },
  {
    path: "/",
    element: <AppLayout.Public />,
    errorElement: <Error404View />,
    children: [
      {
        index: true,
        element: <IndexView />,
        ...withHandle("index"),
      },
      {
        path: "auth",
        element: (
          <MantineProvider theme={rpaTheme} withCSSVariables withGlobalStyles>
            <Outlet />
          </MantineProvider>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="sign-in" replace />,
          },
          {
            path: "sign-in",
            index: true,
            element: (
              <AuthContainer hideRightPane>
                <AuthenticatorView initialState="signIn" />
              </AuthContainer>
            ),
            ...withHandle("sign-in"),
          },
          {
            path: "sign-up",
            element: (
              <AuthContainer>
                <AuthenticatorView initialState="signUp" />
              </AuthContainer>
            ),
            ...withHandle("sign-up"),
          },
          {
            path: "reset-password",
            element: (
              <AuthContainer hideRightPane>
                <AuthenticatorView initialState="resetPassword" />
              </AuthContainer>
            ),
            ...withHandle("reset-password"),
          },
        ],
      },
      {
        path: "design",
        element: <DesignLayout />,
        children: [
          {
            index: true,
            element: <DesignView />,
            ...withHandle("design"),
          },
          {
            path: "v2",
            element: <DesignV2View />,
            ...withHandle("design-v2"),
          },
        ],
      },
    ],
  },
  {
    path: "*",
    element: <Error404View />,
    ...withHandle("not-found"),
  },
]);
